import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import Layout from '../../../components/admin/Layout';
import axios from '../../../utils/axios';
import { BiDish, BiSearch, BiTrash } from 'react-icons/bi';
import { HiOutlinePencil } from 'react-icons/hi';
import { useTranslation } from '../../../context/LanguageContext';

const ItemsIndex = () => {
    const { t, isRTL } = useTranslation();
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const handleDeleteItem = async (itemId) => {
        if (window.confirm(t('admin.confirmDeleteItem'))) {
            try {
                await axios.delete(`/items/${itemId}`);
                toast.success(t('admin.itemDeletedSuccessfully'));
                // Remove the deleted item from the state
                setItems(prevItems => prevItems.filter(item => item.id !== itemId));
            } catch (error) {
                console.error('Error deleting item:', error);
                toast.error(t('admin.failedToDeleteItem'));
            }
        }
    };

    // Effect for fetching items when category changes
    useEffect(() => {
        const fetchItems = async () => {
            try {
                setLoading(true);
                const url = '/items' + (selectedCategory ? `?category_id=${selectedCategory}` : '');
                const response = await axios.get(url);
                setItems(response.data.data || []);
            } catch (error) {
                console.error('Error loading menu items:', error);
                setItems([]);
                toast.error(t('admin.failedToLoadItems'));
            } finally {
                setLoading(false);
            }
        };

        fetchItems();
    }, [selectedCategory, t]); // Re-fetch when selected category or translations change

    // Effect for fetching categories once on mount
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await axios.get('/categories');
                setCategories(response.data.data || []);
            } catch (error) {
                console.error('Error loading categories:', error);
                toast.error(t('admin.failedToLoadCategories'));
            }
        };

        fetchCategories();
    }, [t]); // Include t since it's used for error messages

    const formatPrice = (price) => {
        return new Intl.NumberFormat('ar-SA', {
            style: 'currency',
            currency: 'SAR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(price);
    };

    const filteredItems = items.filter(item => {
        const searchTerm = searchQuery.toLowerCase();
        const matchesSearch = item.name.toLowerCase().includes(searchTerm) || 
                            item.name_ar.toLowerCase().includes(searchTerm) ||
                            item.description?.toLowerCase().includes(searchTerm) ||
                            item.description_ar?.toLowerCase().includes(searchTerm);
        return matchesSearch;
    });

    if (loading) {
        return (
            <Layout>
                <div className="flex justify-center items-center h-[calc(100vh-4rem)]">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-500"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className={`container mx-auto px-4 sm:px-6 lg:px-8 py-8 ${isRTL ? 'rtl' : 'ltr'}`}>
                {/* Header Section */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                    <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                        <h1 className="text-3xl font-bold text-gray-900">{t('admin.items')}</h1>
                        <p className="mt-1 text-sm text-gray-500">{t('admin.manageItems')}</p>
                    </div>
                    <Link
                        to="/admin/items/create"
                        className={`inline-flex items-center px-4 py-2 bg-yellow-500 text-white rounded-lg 
                            shadow-sm hover:bg-yellow-600 transform hover:translate-y-[-1px] transition-all duration-200
                            focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 ${isRTL ? 'flex-row-reverse' : ''}`}
                    >
                        <BiDish className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                        <span>{t('admin.addItem')}</span>
                    </Link>
                </div>

                {/* Search and Filter Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                        {/* Search Input */}
                        <div className={`relative flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <BiSearch className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                className={`block w-full rounded-lg border-gray-200 ${
                                    isRTL ? 'pr-3 pl-10 text-right' : 'pl-10 pr-3'
                                } py-2 focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm`}
                                placeholder={t('admin.searchItems')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        
                        {/* Category Filter */}
                        <div className={`flex flex-wrap gap-2 ${isRTL ? 'justify-end' : 'justify-start'}`}>
                            <button
                                onClick={() => setSelectedCategory('')}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                    selectedCategory === ''
                                        ? 'bg-yellow-500 text-white shadow-sm'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                {t('admin.allItems')}
                            </button>
                            {categories.map(category => (
                                <button
                                    key={category.id}
                                    onClick={() => setSelectedCategory(category.id)}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                        selectedCategory === category.id
                                            ? 'bg-yellow-500 text-white shadow-sm'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    {isRTL ? category.name_ar : category.name}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Items Grid */}
                {filteredItems.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 xl:gap-6">
                        {filteredItems.map(item => (
                            <div 
                                key={item.id} 
                                className="group bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 
                                    border border-gray-100 overflow-hidden"
                            >
                                {item.image ? (
                                    <div className="aspect-w-16 aspect-h-9 bg-gray-100">
                                        <img
                                            src={item.image}
                                            alt={isRTL ? item.name_ar : item.name}
                                            className="w-full h-48 object-cover transform group-hover:scale-105 transition-transform duration-200"
                                        />
                                    </div>
                                ) : (
                                    <div className="aspect-w-16 aspect-h-9 bg-gradient-to-br from-yellow-50 to-yellow-100 
                                        flex items-center justify-center">
                                        <BiDish className="w-16 h-16 text-yellow-300" />
                                    </div>
                                )}
                                
                                <div className="p-5">
                                    <div className={`flex items-start gap-4 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
                                        <div className={`flex-1 min-w-0 ${isRTL ? 'text-right' : 'text-left'}`}>
                                            <h3 className="text-lg font-semibold text-gray-900 truncate group-hover:text-yellow-600 transition-colors duration-200">
                                                {isRTL ? item.name_ar : item.name}
                                            </h3>
                                            <p className="mt-1 text-sm text-gray-500 line-clamp-2">
                                                {isRTL ? item.description_ar : item.description}
                                            </p>
                                        </div>
                                        <div className={`flex flex-col items-end ${isRTL ? 'items-start' : 'items-end'}`}>
                                            <div className="text-lg font-bold text-yellow-600">
                                                {formatPrice(item.price)}
                                            </div>
                                            <div className="flex gap-2 mt-2">
                                                <Link
                                                    to={`/admin/items/${item.id}/edit`}
                                                    className="p-2 text-gray-600 hover:text-yellow-600 transition-colors duration-200"
                                                >
                                                    <HiOutlinePencil className="w-5 h-5" />
                                                </Link>
                                                <button
                                                    onClick={() => handleDeleteItem(item.id)}
                                                    className="p-2 text-gray-600 hover:text-red-600 transition-colors duration-200"
                                                >
                                                    <BiTrash className="w-5 h-5" />
                                                </button>
                                            </div>
                                            <span className={`mt-2 px-3 py-1 rounded-full text-xs font-medium ${
                                                item.is_active 
                                                    ? 'bg-green-100 text-green-800' 
                                                    : 'bg-red-100 text-red-800'
                                            }`}>
                                                {item.is_active ? t('admin.active') : t('admin.inactive')}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="mt-4 pt-4 border-t border-gray-100">
                                        <Link
                                            to={`/admin/items/${item.id}/edit`}
                                            className={`inline-flex items-center justify-center w-full px-4 py-2 
                                                text-sm font-medium text-yellow-600 bg-yellow-50 rounded-lg
                                                hover:bg-yellow-100 hover:text-yellow-700 transition-colors duration-200 
                                                ${isRTL ? 'flex-row-reverse' : ''}`}
                                        >
                                            <HiOutlinePencil className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                                            {t('admin.editItem')}
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-100">
                        <BiDish className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-4 text-lg font-medium text-gray-900">
                            {t('admin.noItemsFound')}
                        </h3>
                        <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
                            {selectedCategory 
                                ? t('admin.noItemsInCategory')
                                : t('admin.getStarted')}
                        </p>
                        <div className="mt-6">
                            <Link
                                to="/admin/items/create"
                                className={`inline-flex items-center px-4 py-2 border border-transparent 
                                    shadow-sm text-sm font-medium rounded-lg text-white bg-yellow-600 
                                    hover:bg-yellow-700 transform hover:translate-y-[-1px] transition-all duration-200
                                    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 
                                    ${isRTL ? 'flex-row-reverse' : ''}`}
                            >
                                <BiDish className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                                {t('admin.addItem')}
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default ItemsIndex;
